import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import { getAdminUsers, adminAction } from "@/services/adminApi";
import type { AdminUser } from "@/types";

const roleTabs = [
  { value: "all", label: "All" },
  { value: "owner", label: "Owners" },
  { value: "staff", label: "Staff" },
  { value: "admin", label: "Admins" },
];

export default function AdminUsers() {
  const { token } = useAuth();
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminUsers(token)
      .then((u) => active && setRows(u))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesRole = role === "all" || r.role === role;
      const matchesQuery = !q || r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
      return matchesRole && matchesQuery;
    });
  }, [rows, query, role]);

  const toggleSuspend = (id: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: r.status === "suspended" ? "active" : "suspended" } : r)),
    );
    flash("User status updated.");
  };

  const resetPassword = async (id: string) => {
    const done = await adminAction(`/admin/users/${id}/reset-password`, {}, token);
    flash(done ? "A temporary password was emailed to the user." : "Could not reset the password. Please try again.");
  };

  const columns: DataColumn<AdminUser>[] = [
    {
      key: "name",
      header: "User",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-200 text-xs font-bold text-foreground-700">
            {r.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground-950">{r.name}</p>
            <p className="truncate text-xs text-foreground-500">{r.email}</p>
          </div>
        </div>
      ),
    },
    { key: "phone", header: "Phone", render: (r) => <span className="text-foreground-700">{r.phone}</span>, nowrap: true },
    {
      key: "role",
      header: "Role",
      nowrap: true,
      render: (r) => {
        const style =
          r.role === "admin"
            ? "bg-secondary-100 text-secondary-900"
            : r.role === "staff"
              ? "bg-accent-100 text-accent-900"
              : "bg-background-100 text-foreground-700";
        const icon =
          r.role === "admin" ? "ri-shield-keyhole-line" : r.role === "staff" ? "ri-user-star-line" : "ri-user-line";
        return (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${style}`}>
            <i className={icon}></i> {r.role}
          </span>
        );
      },
    },
    { key: "listings", header: "Listings", align: "center", render: (r) => <span className="text-foreground-700">{r.listings}</span>, nowrap: true },
    { key: "plan", header: "Plan", render: (r) => <span className="text-foreground-700">{r.plan}</span>, nowrap: true },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    { key: "joinedAt", header: "Joined", render: (r) => <span className="text-foreground-500">{r.joinedAt}</span>, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => resetPassword(r.id)}
            className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-lock-password-line"></i> Reset password
          </button>
          <button
            type="button"
            onClick={() => toggleSuspend(r.id)}
            disabled={r.role !== "owner"}
            className="inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-700 hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap cursor-pointer"
          >
            <i className={r.status === "suspended" ? "ri-play-line" : "ri-forbid-line"}></i>
            {r.status === "suspended" ? "Reactivate" : "Suspend"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="Users" path="/admin/users" />

      <AdminPageHeader
        icon="ri-group-line"
        title="Users"
        subtitle="Everyone with an account — owners, staff and administrators."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search by name or email…">
          <AdminFilterTabs options={roleTabs} value={role} onChange={setRole} />
        </AdminToolbar>
      </div>

      <p className="mt-4 text-sm text-foreground-600">
        <span className="font-semibold text-foreground-950">{filtered.length}</span> of {rows.length} users
      </p>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load users. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="1000px" />
        )}
      </div>
    </>
  );
}